import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE, PHONE_LINK } from "../data";

const LINKS = [
  { label: "Home", path: "/", id: "nav-home" },
  { label: "Servizi", path: "/servizi", id: "nav-servizi" },
  { label: "Galleria Lavori", path: "/galleria", id: "nav-galleria" },
  { label: "Recensioni", path: "/recensioni", id: "nav-recensioni" },
  { label: "Contatti", path: "/contatti", id: "nav-contatti" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 glass-panel border-b border-gold/10" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <Logo className="h-10 w-10" />
          <span className="leading-none">
            <span className="block font-display font-bold text-lg tracking-wide text-cream">EDILKREA</span>
            <span className="block font-mono text-[10px] tracking-[0.3em] text-gold/80 uppercase">Technoimpianti</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.path}
              data-testid={l.id}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-gold" : "text-cream/70 hover:text-cream"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 right-0 h-[2px] bg-copper"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_LINK}
            data-testid="nav-phone-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:bg-copper hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4" />
            {PHONE}
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gold/20 text-cream"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-gold/10 glass-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`${l.id}-mobile`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-3 font-display text-2xl font-bold uppercase tracking-wide ${
                        isActive ? "text-gold" : "text-cream/80"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={PHONE_LINK}
                data-testid="nav-phone-cta-mobile"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink"
              >
                <Phone className="h-4 w-4" />
                {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
